import type { MessageValue } from 'contexts/intl/intlContext';
import type { IntlKey } from 'locales';
import { NextWorkflowError } from 'models/NextWorkflowError';
import type { HttpError } from 'utils/http.old/httpClient.model';
export const defaultTechnicalErrorKey: IntlKey = 'quote.errorMessages.TechnicalError';

export interface ApiErrorMessage {
  message: string;
  values?: Record<string, MessageValue>;
  newVersion?: number;
  status?: number;
  correlationId?: string;
  traceId?: string;
  functionalErrorType?: 'rfq-version-mismatch';
}

interface ToApiErrorMessageOptions {
  handleHttpTimeout: boolean;
  timeoutErrorMessage: IntlKey;
}

const defaultToApiErrorMessageOptions: ToApiErrorMessageOptions = {
  handleHttpTimeout: false,
  timeoutErrorMessage: defaultTechnicalErrorKey,
};

/** @deprecated */
export function toApiErrorMessage(
  error: Error | (HttpError & WithPayload),
  customOptions: Partial<ToApiErrorMessageOptions> = {},
): ApiErrorMessage {
  const options: ToApiErrorMessageOptions = { ...defaultToApiErrorMessageOptions, ...customOptions };

  if (isHttpError<{ newVersion: number }>(error)) {
    const newVersion = error.payload?.newVersion ?? error.newVersion;

    if (options.handleHttpTimeout && error.status === 504) {
      return {
        message: options.timeoutErrorMessage,
        newVersion,
        correlationId: error.correlationId,
        traceId: error.traceId,
      };
    }

    if (error.status === 400) {
      if ((error.response ?? error.payload)?.errorCode === 'VERSION_MISMATCH') {
        return {
          message: defaultTechnicalErrorKey,
          newVersion,
          status: 400,
          correlationId: error.correlationId,
          traceId: error.traceId,
          functionalErrorType: 'rfq-version-mismatch',
        };
      }

      if (error.payload !== undefined && Array.isArray(error.payload)) {
        return {
          message: 'priceImprovement.error.backend',
          traceId: error.traceId,
          values: {
            origin: error.payload?.join(','),
          },
        };
      }
    }

    return {
      message: error.message,
      newVersion,
      correlationId: error.correlationId,
      traceId: error.traceId,
    };
  } else if (isHttpTimeoutError(error)) {
    return {
      message: options.timeoutErrorMessage,
      values: { timeInMillis: error.timeout },
      correlationId: error.correlationId,
    };
  } else if (isTimeoutError(error)) {
    return {
      message: error.message,
      values: { timeInMillis: error.timeout },
    };
  } else {
    return { message: error.message };
  }
}

type WithPayload<T = unknown> = { payload?: T };
export function isHttpError<T = unknown>(error: Error): error is HttpError & WithPayload<T> {
  return error.name === 'AjaxError' || error.name === 'HttpError';
}

export function isHttpTimeoutError(error: Error): error is HttpTimeoutError {
  if (error.name === 'HttpTimeoutError') {
    return true;
  }

  if (isHttpError(error) && error.status === 504) {
    return true;
  }

  if (isHttpError(error) && error.status === 0) {
    return true;
  }

  return false;
}

export function isTimeoutError(error: Error): error is TimeoutError {
  return error.name === 'TimeoutError';
}

/** @deprecated */
export class HttpTimeoutError extends Error {
  public name = 'HttpTimeoutError';
  constructor(
    public timeout: number,
    public correlationId: string = '',
  ) {
    super();
  }
}

/** @deprecated */
export class TimeoutError extends Error {
  public name = 'TimeoutError';
  constructor(
    public timeout: number,
    message: IntlKey = defaultTechnicalErrorKey,
  ) {
    super(message);
  }
}

/** @deprecated */
export class EpicApiError extends Error {
  public name = 'ApiError';
  constructor(
    message: IntlKey = defaultTechnicalErrorKey,
    public cause: Error,
  ) {
    super(message);
  }
}
export function getErrorRejections(err: any) {
  if (isErrorWithRejections(err)) {
    return err.payload.rejections;
  }
  return [];
}
function isErrorWithRejections(err: any): err is { payload: { rejections: NextWorkflowError[] } } {
  return err?.payload?.rejections !== undefined;
}
